/**
 * CreateCaseFile Page
 *
 * Allows existing users to create additional CaseFiles.
 * Uses the shared CaseFileCreationForm component for the form logic.
 *
 * For designated_agent authority type, the user selects an authorization
 * method in the form (in-app, upload, or email). After creation:
 * - in_app → Shows InAppAuthorizationForm inline
 * - upload → Shows UploadAuthorizationForm inline
 * - email  → Routes to dashboard with locked CaseFile
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useCaseFile } from '../contexts/CaseFileContext';
import { CaseFileCreationForm, CaseFileFormData, DesignatedAgentAuthMethod } from '../components/casefile';
import { InAppAuthorizationForm, UploadAuthorizationForm } from '../components/authorization';
import { ArrowLeft } from 'lucide-react';

interface CreateCaseFileResponse {
  success: boolean;
  caseFileId?: string;
  message?: string;
}

interface CreatedCaseFileInfo {
  caseFileId: string;
  name: string;
  dateOfBirth: string;
  careRecipientEmail?: string;
  authorizationMethod: DesignatedAgentAuthMethod;
}

export function CreateCaseFile(): JSX.Element {
  const navigate = useNavigate();
  const { refreshCaseFiles } = useCaseFile();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Post-creation state for in-app/upload authorization flows
  const [createdCaseFile, setCreatedCaseFile] = useState<CreatedCaseFileInfo | null>(null);

  const handleCreateCaseFile = async (formData: CaseFileFormData) => {
    setIsCreating(true);
    setError(null);

    try {
      const createCaseFile = httpsCallable<CaseFileFormData, CreateCaseFileResponse>(
        functions,
        'createCaseFile'
      );

      const result = await createCaseFile(formData);

      if (result.data.success) {
        const authMethod = formData.designatedAgentData?.authorizationMethod;

        // For designated_agent with in-app or upload, show the form inline
        if (formData.authorityType === 'designated_agent' && authMethod && authMethod !== 'email') {
          setCreatedCaseFile({
            caseFileId: result.data.caseFileId!,
            name: formData.name,
            dateOfBirth: formData.dateOfBirth,
            careRecipientEmail: formData.designatedAgentData?.careRecipientEmail,
            authorizationMethod: authMethod,
          });
          return; // Don't navigate — show auth form
        }

        // For email or other authority types, navigate to dashboard
        let message = `CaseFile created for ${formData.name}`;
        if (formData.authorityType === 'designated_agent' && authMethod === 'email') {
          message = `CaseFile created for ${formData.name}. An authorization request has been sent to ${formData.designatedAgentData?.careRecipientEmail}. The CaseFile will remain locked until they authorize.`;
        } else if (formData.authorityType === 'legal_guardian') {
          message = `CaseFile created for ${formData.name}. Please upload guardianship documentation to complete setup.`;
        }

        await refreshCaseFiles();
        navigate('/', {
          state: {
            message: message,
            caseFileId: result.data.caseFileId,
          },
        });
      } else {
        setError(result.data.message || 'Failed to create CaseFile');
        throw new Error(result.data.message || 'Failed to create CaseFile');
      }
    } catch (err) {
      console.error('Error creating CaseFile:', err);
      if (err instanceof Error) {
        setError(err.message || 'Failed to create CaseFile. Please try again.');
      } else {
        setError('Failed to create CaseFile. Please try again.');
      }
      throw err;
    } finally {
      setIsCreating(false);
    }
  };

  // Handle authorization completion (in-app or upload) — CaseFile unlocked
  const handleAuthorizationComplete = async () => {
    await refreshCaseFiles();
    navigate('/', {
      state: {
        message: `CaseFile for ${createdCaseFile!.name} has been authorized and unlocked.`,
        caseFileId: createdCaseFile!.caseFileId,
      },
    });
  };

  // Handle cancel from auth form — go to dashboard with locked CaseFile
  const handleAuthorizationCancel = async () => {
    await refreshCaseFiles();
    navigate('/', {
      state: {
        message: `CaseFile created for ${createdCaseFile!.name}. You can complete authorization from the CaseFile page.`,
        caseFileId: createdCaseFile!.caseFileId,
      },
    });
  };

  // =========================================================================
  // Render: In-App Authorization Form (post-creation)
  // =========================================================================
  if (createdCaseFile && createdCaseFile.authorizationMethod === 'in_app') {
    return (
      <div className="min-h-screen bg-airy">
        <header className="bg-white border-b border-soft-neutral">
          <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 flex justify-between items-center">
            <h1 className="text-2xl font-display text-deep-forest font-light">Hesvara</h1>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={handleAuthorizationCancel}
            className="btn-ghost btn-sm flex items-center gap-1 text-muted mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Skip — complete later
          </button>
          <InAppAuthorizationForm
            caseFileId={createdCaseFile.caseFileId}
            caseFileName={createdCaseFile.name}
            careRecipientName={createdCaseFile.name}
            careRecipientDob={createdCaseFile.dateOfBirth}
            careRecipientEmail={createdCaseFile.careRecipientEmail}
            onComplete={handleAuthorizationComplete}
            onCancel={handleAuthorizationCancel}
          />
        </main>
      </div>
    );
  }

  // =========================================================================
  // Render: Upload Authorization Form (post-creation)
  // =========================================================================
  if (createdCaseFile && createdCaseFile.authorizationMethod === 'upload') {
    return (
      <div className="min-h-screen bg-airy">
        <header className="bg-white border-b border-soft-neutral">
          <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 flex justify-between items-center">
            <h1 className="text-2xl font-display text-deep-forest font-light">Hesvara</h1>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={handleAuthorizationCancel}
            className="btn-ghost btn-sm flex items-center gap-1 text-muted mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Skip — complete later
          </button>
          <UploadAuthorizationForm
            caseFileId={createdCaseFile.caseFileId}
            caseFileName={createdCaseFile.name}
            careRecipientName={createdCaseFile.name}
            careRecipientDob={createdCaseFile.dateOfBirth}
            careRecipientEmail={createdCaseFile.careRecipientEmail}
            onComplete={handleAuthorizationComplete}
            onCancel={handleAuthorizationCancel}
          />
        </main>
      </div>
    );
  }

  // =========================================================================
  // Render: CaseFile Creation Form (default)
  // =========================================================================
  return (
    <div className="min-h-screen bg-airy">
      <header className="bg-white border-b border-soft-neutral">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <h1 className="text-2xl font-display text-deep-forest font-light">Hesvara</h1>
          <button
            onClick={() => navigate('/')}
            className="btn-ghost btn-sm"
          >
            &larr; Back to Dashboard
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="card">
          <div className="card-header">
            <h2 className="text-xl font-semibold text-ink">Create New CaseFile</h2>
            <p className="text-muted mt-1">
              Enter information about the care recipient. You can add more details later.
            </p>
          </div>

          <div className="card-body">
            {error && (
              <div className="alert alert-error mb-6" role="alert">{error}</div>
            )}

            <CaseFileCreationForm
              onSubmit={handleCreateCaseFile}
              isLoading={isCreating}
              showInsurance={true}
              submitButtonText="Create CaseFile"
              showBackButton={true}
              onBack={() => navigate('/')}
              backButtonText="Cancel"
            />
          </div>
        </div>
      </main>
    </div>
  );
}

export default CreateCaseFile;
